import { useState, useEffect, useRef } from 'react';

const JobCarousel = ({ jobs }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedJob, setExpandedJob] = useState(null);
  
  // Auto-advance the carousel every 4 seconds if no job is expanded
  useEffect(() => {
    if (expandedJob === null && jobs && jobs.length > 2) {
      const interval = setInterval(() => {
        setActiveIndex((current) => (current + 1) % (jobs.length - 2));
      }, 4000);
      
      return () => clearInterval(interval);
    }
  }, [jobs, expandedJob]);
  
  // Manual navigation
  const goToSlide = (index) => {
    setActiveIndex(index);
  };
  
  const nextSlide = () => {
    if (jobs && jobs.length > 2) {
      setActiveIndex((current) => (current + 1) % (jobs.length - 2));
    }
  };
  
  const prevSlide = () => {
    if (jobs && jobs.length > 2) {
      setActiveIndex((current) => (current - 1 + (jobs.length - 2)) % (jobs.length - 2));
    }
  };
  
  // Toggle job description
  const toggleJobDetails = (index) => {
    if (expandedJob === index) {
      setExpandedJob(null);
    } else {
      setExpandedJob(index);
    }
  };
  
  return (
    <div className="relative w-full mt-3 mb-3">
      {/* Carousel container */}
      <div className="overflow-hidden rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
        <div className="relative">
          {/* Carousel items - showing 3 at once */}
          <div 
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${activeIndex * (100/3)}%)` }}
          >
            {jobs.map((job, index) => (
              <div key={index} className="min-w-[33.333%] p-3">
                <div 
                  className={`flex flex-col items-center cursor-pointer transition-all duration-300 ${
                    expandedJob === index ? 'scale-105' : 'hover:scale-105'
                  }`}
                  onClick={() => toggleJobDetails(index)}
                >
                  <div className="w-12 h-12 mb-2 rounded-full flex items-center justify-center overflow-hidden bg-white dark:bg-gray-700">
                    <img 
                      src={job.logo} 
                      alt={`${job.company} logo`} 
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                      width="48"
                      height="48"
                    />
                  </div>
                  <h3 className="text-sm font-bold text-center">{job.company}</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center mt-1">{job.role}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Navigation buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/3 transform -translate-y-1/2 bg-white dark:bg-gray-800 rounded-r-full p-1 shadow-md z-10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Previous slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/3 transform -translate-y-1/2 bg-white dark:bg-gray-800 rounded-l-full p-1 shadow-md z-10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Next slide"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Expanded job description (full width) */}
      {expandedJob !== null && (
        <div className="absolute left-0 right-0 mt-2 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-20">
          <div className="flex items-start">
            <div className="w-10 h-10 mr-3 rounded-full overflow-hidden flex-shrink-0">
              <img 
                src={jobs[expandedJob].logo} 
                alt={`${jobs[expandedJob].company} logo`} 
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-bold">{jobs[expandedJob].company}</h3>
              <p className="text-xs text-gray-600 dark:text-gray-400">{jobs[expandedJob].role}</p>
              <p className="text-xs text-gray-700 dark:text-gray-300 mt-2">{jobs[expandedJob].description}</p>
            </div>
            <button 
              className="ml-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              onClick={() => setExpandedJob(null)}
              aria-label="Close description"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      {/* Indicator dots */}
      <div className="flex justify-center mt-2 space-x-1">
        {jobs.slice(0, jobs.length - 2).map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-2 h-2 rounded-full ${
              index === activeIndex
                ? 'bg-gray-800 dark:bg-gray-200'
                : 'bg-gray-300 dark:bg-gray-600'
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default JobCarousel;